/**
 * "Votre garde-manger" shelf: the recipes that use the most of what is in the
 * pantry right now.
 *
 * Extracted from the recipes screen (#668). The inline version lower-cased every
 * ingredient line once per pantry item — 1615 recipes × 232 items × 12 lines of
 * allocations on every mount and every pantry change. Same matching rules:
 * substring in either direction, case-insensitive, counted per pantry item.
 */

export interface PantryShelfRecipe {
  id: string;
  ingredients?: string[];
}

export const PANTRY_SHELF_SIZE = 12;

/**
 * Which of `activeNames` a recipe uses: a name inside an ingredient line, or a
 * short line inside a name. Returns their indices into `activeNames`.
 *
 * The one matching rule for the home shelf and the search's pantry row (#847),
 * so a recipe cannot be on one and missing from the other.
 */
function pantryNameIndicesUsedBy(
  recipe: PantryShelfRecipe,
  activeNames: string[],
  maxNameLen: number,
): number[] {
  const lines = (recipe.ingredients ?? []).map(ing => ing.toLowerCase());
  if (lines.length === 0) return [];
  // "Some line contains the name" is one search over the lines joined by a
  // separator no pantry name contains, instead of one search per line.
  const joined = lines.join('\n');
  const shortLines = lines.filter(line => line.length <= maxNameLen);
  const used: number[] = [];
  for (let i = 0; i < activeNames.length; i++) {
    const inv = activeNames[i];
    if (joined.includes(inv)) {
      used.push(i);
      continue;
    }
    for (const line of shortLines) {
      if (inv.includes(line)) {
        used.push(i);
        break;
      }
    }
  }
  return used;
}

/**
 * A pantry name can only CONTAIN a whole ingredient line when the line is no
 * longer than the longest name; most lines carry a quantity and are longer.
 */
function longestName(names: string[]): number {
  let max = 0;
  for (const name of names) if (name.length > max) max = name.length;
  return max;
}

/**
 * @param activeNames lower-cased names of the ACTIVE pantry rows only. Eaten
 *   and discarded rows must not put a recipe on this shelf.
 */
export function rankRecipesByPantryMatch<T extends PantryShelfRecipe>(
  recipes: T[],
  activeNames: string[],
): T[] {
  if (activeNames.length === 0) return [];
  const maxNameLen = longestName(activeNames);

  const scored: Array<{ recipe: T; matchCount: number }> = [];
  for (const recipe of recipes) {
    const matchCount = pantryNameIndicesUsedBy(recipe, activeNames, maxNameLen).length;
    if (matchCount > 0) scored.push({ recipe, matchCount });
  }
  return scored
    .sort((a, b) => b.matchCount - a.matchCount)
    .slice(0, PANTRY_SHELF_SIZE)
    .map(({ recipe }) => recipe);
}

/**
 * The pantry rows each recipe uses, spelled as the pantry spells them
 * (« Tomates broyées Aylmer »), for the search's « Votre garde-manger » row and
 * the line under its cards. Recipes that use nothing are absent from the map.
 *
 * @param activeItems the ACTIVE pantry rows only, as for the shelf above.
 */
export function pantryUsesByRecipe(
  recipes: PantryShelfRecipe[],
  activeItems: ReadonlyArray<{ name: string }>,
): Map<string, string[]> {
  const uses = new Map<string, string[]>();
  // One entry per distinct name: two rows of « Citrons » are one thing used.
  const display: string[] = [];
  const lowered: string[] = [];
  const seen = new Set<string>();
  for (const item of activeItems) {
    const name = item.name?.trim();
    if (!name) continue;
    const key = name.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    display.push(name);
    lowered.push(key);
  }
  if (lowered.length === 0) return uses;
  const maxNameLen = longestName(lowered);
  for (const recipe of recipes) {
    const used = pantryNameIndicesUsedBy(recipe, lowered, maxNameLen);
    if (used.length > 0) uses.set(recipe.id, used.map(i => display[i]));
  }
  return uses;
}
